import { useState, type ChangeEvent } from 'react';
import { IconArrowDown, IconArrowUp, IconArrowsExchange, IconBell, IconDownload, IconEye, IconFileTypePdf, IconLayoutSidebarRight, IconLink, IconLock, IconPlus, IconSend, IconUpload } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { Button, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { useWide } from '../sales/useWide';
import { CONVERT0, FEES0, FX, JOBS, MADE0, MERGE0, OCR_TEXT, SHIPPERS0, SUB, TOOLS, fmt, fxUnit, num, splitCosts, type Fee, type FileTy, type Made, type MergeFile, type Shipper, type ToolKey } from './utility';
import { useT } from '@hana/i18n';
import './utility.i18n';
import s from './utility.module.css';

// Mint-Utility (1536 / 1920): Tiện ích · Ghép file · OCR · Word/Excel → PDF · Chia phí theo shipper · Tỷ giá. Chỉ đọc, nút ghi báo toast.

const TY: Record<string, string> = { PDF: s.tyErr!, XLSX: s.tyOk!, XLS: s.tyOk!, DOCX: s.tyPri!, DOC: s.tyPri! };
const tyCls = (ty: string) => `${s.ty} ${TY[ty] ?? s.tyWarn}`;

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
function readTool(): ToolKey {
  const v = new URLSearchParams(location.search).get('tool');
  return TOOLS.some(([k]) => k === v) ? (v as ToolKey) : new URLSearchParams(location.search).get('vw') === '1920' ? 'split' : 'merge';
}

export function Utility() {
  const toastFn = useToast();
  const toast = (msg: string) => toastFn({ msg });
  const tt = useT();
  const wide = useWide();
  const forced = readState();
  const state: ScreenState = forced ?? 'ready';
  const empty = state === 'empty';
  const [tool, setTool] = useState<ToolKey>(() => (wide && !new URLSearchParams(location.search).get('tool') ? 'split' : readTool()));
  const [dense, setDense] = useState(false);
  const [right, setRight] = useState(false);
  const dockOpen = wide || right;
  const rowH = dense ? 40 : 48;

  const [mf, setMf] = useState<MergeFile[]>(empty ? [] : MERGE0);
  const [mName, setMName] = useState('Merged_SE260639142');
  const [mA4, setMA4] = useState(true);
  const [mBm, setMBm] = useState(false);
  const [made, setMade] = useState<Made[]>(empty ? [] : MADE0);
  const [ocr, setOcr] = useState<{ file: string | null; done: boolean; text: string }>({ file: empty ? null : 'Scan_PackingList_NAMMY.jpg', done: false, text: OCR_TEXT });
  const [cf, setCf] = useState(empty ? [] : CONVERT0);
  const [cWay, setCWay] = useState<'m365' | 'local'>(state === 'error' ? 'local' : 'm365');
  const [cDone, setCDone] = useState<Record<string, boolean>>({});
  const [sh, setSh] = useState<Shipper[]>(empty ? [] : SHIPPERS0);
  const [fe, setFe] = useState<Fee[]>(FEES0);
  const [basis, setBasis] = useState<'cbm' | 'gw'>('cbm');
  const [sDate, setSDate] = useState('22/Oct/2026');
  const [sRate, setSRate] = useState('26.454');
  const [sQ, setSQ] = useState('');
  const [sOpen, setSOpen] = useState(false);
  const [fx, setFx] = useState({ amt: '1.000', from: 'USD', to: 'VND', type: 'transfer' as 'transfer' | 'sell', date: '22/Oct/2026' });

  const soon = (m: string) => () => toast(tt('ut.soon', { m }));
  const noWrite = () => toast(tt('ut.noWrite'));
  const locked = (k: ToolKey) => state === 'noperm' && (k === 'ocr' || k === 'convert');
  const loading = state === 'loading';
  const showTool = !loading && !locked(tool);
  const addMade = (m: Made) => setMade((a) => [m, ...a]);

  const pri = tool === 'merge'
    ? { k: tt('ut.priMerge'), dis: mf.length < 2, run: () => { const n = `${mName.trim() || 'Merged'}.pdf`; addMade({ ty: 'PDF', name: n, meta: `Ghép file · ${mf.reduce((a, f) => a + f.pages, 0)} trang · vừa xong` }); toast(`Đã ghép ${n}`); } }
    : tool === 'ocr' ? { k: tt('ut.priOcr'), dis: !ocr.file, run: () => { setOcr((o) => ({ ...o, done: true })); toast(`Ask HANA đã đọc xong ${ocr.file ?? ''}`); } }
    : tool === 'convert' ? { k: tt('ut.priConv'), dis: cf.length === 0, run: () => { setCDone(Object.fromEntries(cf.map((f) => [f.name, true]))); toast('Đã chuyển sang PDF'); } }
    : tool === 'split' ? { k: tt('ut.priPdf'), dis: sh.length === 0, run: () => { const n = `ChiaPhi_${sh[0]?.job ?? 'consol'}_221026.pdf`; addMade({ ty: 'PDF', name: n, meta: `Chia phí · ${sh.length} shipper · vừa xong` }); toast(tt('ut.exported', { n })); } }
    : { k: '', dis: true, run: () => {} };

  const onFiles = (e: ChangeEvent<HTMLInputElement>, fallback: string): string => e.target.files?.[0]?.name ?? fallback;
  const extTy = (n: string): FileTy => { const x = (n.split('.').pop() ?? 'pdf').toUpperCase(); return (x === 'JPEG' ? 'JPG' : x) as FileTy; };

  // ----- Ghép file -----
  const move = (i: number, d: number) => setMf((a) => { const b = a.slice(); const t = b[i]!; b[i] = b[i + d]!; b[i + d] = t; return b; });
  const merge = (
    <div className={s.row2}>
      <section className={`${s.card} ${s.grow}`} aria-label={tt('ut.merge')}>
        <div className={s.ch}><h2>{tt('ut.merge')}</h2><span className={s.lb12}>{tt('ut.mfCount', { n: mf.length, p: mf.reduce((a, f) => a + f.pages, 0) })}</span></div>
        <div className={s.cb}>
          <label className={s.drop} style={{ minHeight: mf.length ? 98 : 150 }}>
            <input type="file" multiple className={s.vh} aria-label={tt('ut.pickMerge')} onChange={(e) => { const n = onFiles(e, 'CO_FormE_SE260639142.pdf'); setMf((a) => [...a, { name: n, ty: extTy(n), pages: 1, size: '230 KB' }]); toast(`Đã thêm ${n}`); }} />
            <IconUpload size={26} stroke={1.8} aria-hidden="true" />
            <b>{tt('ut.drop')}</b>
            <span className={s.lb12}>{tt('ut.dropTypes')}</span>
          </label>
          {mf.map((f, i) => (
            <div key={f.name} className={s.frow} style={{ minHeight: rowH }}>
              <span className={s.no}>{i + 1}</span>
              <span className={tyCls(f.ty)} aria-hidden="true">{f.ty}</span>
              <div className={s.fn}><div className={s.fname}>{f.name}</div><div className={s.lb12}>{tt('ut.pgSize', { p: f.pages, z: f.size })}</div></div>
              <button type="button" className={s.ib} aria-label={tt('ut.up', { n: f.name })} disabled={i === 0} onClick={() => move(i, -1)}><IconArrowUp size={15} stroke={2} aria-hidden="true" /></button>
              <button type="button" className={s.ib} aria-label={tt('ut.down', { n: f.name })} disabled={i === mf.length - 1} onClick={() => move(i, 1)}><IconArrowDown size={15} stroke={2} aria-hidden="true" /></button>
              <button type="button" className={s.ibx} aria-label={tt('ut.rmFile', { n: f.name })} onClick={() => { setMf((a) => a.filter((_, j) => j !== i)); toast(tt('ut.removed', { n: f.name })); }}>×</button>
            </div>
          ))}
          {mf.length === 0 && <div className={s.none}>{tt('ut.noMerge')}</div>}
        </div>
      </section>
      <section className={`${s.card} ${s.side}`} aria-label={tt('ut.mergeOpt')}>
        <h2 className={s.h2}>{tt('ut.options')}</h2>
        <label className={s.fld}><div title={tt('ut.mergeTip')}>{tt('ut.mergedName')}</div><input className={s.inp} value={mName} onChange={(e) => setMName(e.target.value)} /></label>
        <label className={s.chk}><input type="checkbox" checked={mA4} onChange={(e) => setMA4(e.target.checked)} /><span>{tt('ut.a4')}</span></label>
        <label className={s.chk}><input type="checkbox" checked={mBm} onChange={(e) => setMBm(e.target.checked)} /><span>{tt('ut.bookmark')}</span></label>
      </section>
    </div>
  );

  // ----- OCR -----
  const ocrView = (
    <div className={s.row2}>
      <section className={`${s.card} ${s.ocrL}`} aria-label={tt('ut.src')}>
        <div className={s.ch}><h2>{tt('ut.src')}</h2><label className={s.pick}><input type="file" className={s.vh} aria-label={tt('ut.pickOcr')} onChange={(e) => setOcr({ file: onFiles(e, 'Scan_Invoice_SAVITA.pdf'), done: false, text: OCR_TEXT })} />{tt('ut.pick')}</label></div>
        <div className={`${s.cb} ${s.cbFill}`}>
          {ocr.file ? (
            <>
              <div className={s.frow0}><span className={`${s.ty} ${s.tyPri}`} aria-hidden="true">{(ocr.file.split('.').pop() ?? '').toUpperCase()}</span><div><div className={s.fname}>{ocr.file}</div><div className={s.lb12}>{tt('ut.ocrMeta')}</div></div></div>
              <div className={s.pv} aria-hidden="true"><div className={s.pg}>{[46, 30, 92, 84, 88, 70, 90, 64].map((w, i) => <i key={i} style={{ width: `${w}%` }} />)}<div className={s.cells}>{Array.from({ length: 12 }, (_, i) => <i key={i} />)}</div></div></div>
            </>
          ) : (
            <label className={s.drop} style={{ flexGrow: 1 }}><input type="file" className={s.vh} aria-label={tt('ut.pickOcr')} onChange={(e) => setOcr({ file: onFiles(e, 'Scan_Invoice_SAVITA.pdf'), done: false, text: OCR_TEXT })} /><b>{tt('ut.dropOcr')}</b><span className={s.lb12}>{tt('ut.ocrSub')}</span></label>
          )}
        </div>
      </section>
      <section className={`${s.card} ${s.grow}`} aria-label={tt('ut.ocrRes')}>
        <div className={s.ch}>
          <h2 className={s.grow}>{tt('ut.result')}</h2>
          {([[tt('ut.stRead'), !!ocr.file], [tt('ut.stOcr'), ocr.done], [tt('ut.stDone'), ocr.done]] as [string, boolean][]).map(([k, on], i) => <span key={k} className={`${s.step} ${on ? s.stepOn : ''}`}><i>{on ? '✓' : i + 1}</i>{k}</span>)}
        </div>
        <div className={`${s.cb} ${s.cbFill}`}>
          {ocr.done ? (
            <>
              <textarea className={s.ta} aria-label={tt('ut.ocrText')} value={ocr.text} onChange={(e) => setOcr((o) => ({ ...o, text: e.target.value }))} />
              <div className={s.acts}><span className={`${s.lb12} ${s.grow}`}>{tt('ut.exportAs')}</span>
                {['Word', 'Excel', 'PDF'].map((k) => <Button key={k} icon={<IconDownload size={16} aria-hidden="true" />} onClick={() => { const n = `OCR_${(ocr.file ?? '').replace(/\.[^.]+$/, '')}.${k === 'Word' ? 'docx' : k === 'Excel' ? 'xlsx' : 'pdf'}`; addMade({ ty: k === 'Word' ? 'DOCX' : k === 'Excel' ? 'XLSX' : 'PDF', name: n, meta: tt('ut.ocrJust') }); toast(tt('ut.exported', { n })); }}>{k}</Button>)}
              </div>
            </>
          ) : <div className={s.wait}><b>{tt('ut.noOcr')}</b><span>{tt('ut.ocrHint')}</span></div>}
        </div>
      </section>
    </div>
  );

  // ----- Word / Excel → PDF -----
  const convert = (
    <div className={s.row2}>
      <section className={`${s.card} ${s.grow}`} aria-label={tt('ut.office')}>
        <div className={s.ch}><h2>{tt('ut.wordExcel')}</h2><label className={s.pick}><input type="file" multiple className={s.vh} aria-label={tt('ut.pickOffice')} onChange={(e) => { const n = onFiles(e, 'SOA_RUITONGVN-SC_T10.xlsx'); setCf((a) => [...a, { name: n, ty: extTy(n), meta: '1 sheet' }]); }} />{tt('ut.addFile')}</label></div>
        <div className={s.cb}>
          {cf.map((f) => (
            <div key={f.name} className={s.frow} style={{ minHeight: rowH }}>
              <span className={tyCls(f.ty)} aria-hidden="true">{f.ty}</span>
              <div className={s.fn}><div className={s.fname}>{f.name}</div><div className={s.lb12}>{f.meta}</div></div>
              <span className={`${s.lb12} ${cDone[f.name] ? s.okTx : ''}`}>{cDone[f.name] ? tt('ut.converted') : tt('ut.pending')}</span>
              {cDone[f.name] && <Button size="sm" icon={<IconFileTypePdf size={16} aria-hidden="true" />} onClick={soon('Xem PDF')}>PDF</Button>}
              <button type="button" className={s.ibx} aria-label={tt('ut.rmFile', { n: f.name })} onClick={() => setCf((a) => a.filter((x) => x.name !== f.name))}>×</button>
            </div>
          ))}
          {cf.length === 0 && <div className={s.none}>{tt('ut.noConv')}</div>}
        </div>
      </section>
      <section className={`${s.card} ${s.side}`} aria-label={tt('ut.way')}>
        <h2 className={s.h2} title={tt('ut.wayTip')}>{tt('ut.way')}</h2>
        {([['m365', 'Microsoft 365', tt('ut.m365Sub')], ['local', tt('ut.local'), tt('ut.localSub')]] as const).map(([k, l, v]) => (
          <label key={k} className={`${s.way} ${cWay === k ? s.wayOn : ''}`}><input type="radio" name="cway" checked={cWay === k} onChange={() => setCWay(k)} /><div><b>{l}</b><div className={s.lb12}>{v}</div></div></label>
        ))}
      </section>
    </div>
  );

  // ----- Chia phí theo shipper -----
  const rate = num(sRate);
  const r = splitCosts(sh, fe, basis, rate);
  const setShip = (i: number, p: Partial<Shipper>) => setSh((a) => a.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const setFee = (i: number, p: Partial<Fee>) => setFe((a) => a.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const q = sQ.trim().toLowerCase();
  const jobs = JOBS.filter((j) => !sh.some((x) => x.job === j.id)).filter((j) => !q || `${j.id} ${j.kh}`.toLowerCase().includes(q));
  const split = (
    <section className={`${s.card} ${s.grow}`} aria-label={tt('ut.splitAria')}>
      <div className={s.sTop}>
        <label className={s.fld}><div>{tt('ut.splitDate')}</div><input className={s.inp} style={{ width: 130 }} value={sDate} onChange={(e) => setSDate(e.target.value)} /></label>
        <label className={s.fld}><div>{tt('ut.rate')}</div><input className={s.inp} style={{ width: 110, textAlign: 'right' }} value={sRate} onChange={(e) => setSRate(e.target.value)} /></label>
        <Button className={s.h38} icon={<IconDownload size={16} aria-hidden="true" />} onClick={() => { setSRate('26.454'); toast('Đã lấy giá bán Vietcombank 26.454'); }}>{tt('ut.getVcb')}</Button>
        <div className={s.fld}><div>{tt('ut.basis')}</div><Segmented kind="toggle" label={tt('ut.basisAria')} value={basis} onChange={setBasis} items={[{ key: 'cbm', label: 'CBM' }, { key: 'gw', label: 'GW (kg)' }]} /></div>
        <div className={`${s.fld} ${s.jobSearch}`}>
          <div>{tt('ut.addFromJob')}</div>
          <label className={s.sbox}><input aria-label={tt('ut.jobSearch')} placeholder={tt('ut.jobPh')} value={sQ} onChange={(e) => { setSQ(e.target.value); setSOpen(true); }} onFocus={() => setSOpen(true)} /></label>
          {sOpen && (
            <div className={s.pop} role="listbox" aria-label={tt('ut.lclJob')}>
              {jobs.map((j) => <button key={j.id} type="button" role="option" aria-selected={false} onClick={() => { setSh((a) => [...a, { name: j.kh, cbm: fmt(j.cbm, 1), gw: fmt(j.gw), job: j.id }]); setSQ(''); setSOpen(false); toast(tt('ut.shAdded', { k: j.kh, j: j.id })); }}><span><b>{j.id}</b> · {j.kh}</span><span className={s.lb12}>{j.sub}</span></button>)}
              {jobs.length === 0 && <div className={s.none}>{tt('ut.noJob')}</div>}
            </div>
          )}
        </div>
      </div>
      <div className={`${s.cb} ${s.sBody}`}>
        <div>
          <div className={s.sh}><h3>Shipper · {sh.length}</h3><span className={s.lb12}>{tt('ut.total')} {basis === 'gw' ? `${fmt(r.totB)} kg` : `${fmt(r.totB, 1)} CBM`}</span></div>
          <div className={s.ships}>
            {sh.map((x, i) => (
              <div key={`${x.job ?? 'n'}${i}`} className={s.ship}>
                <div className={s.shH}><span className={s.link12}>{tt('ut.shipper', { i: i + 1 })}</span><span className={s.b12}>{`${(Math.round((r.pct[i] ?? 0) * 1000) / 10).toString().replace('.', ',')}%`}</span><button type="button" aria-label={tt('ut.rmShip', { i: i + 1 })} onClick={() => { setSh((a) => a.filter((_, j) => j !== i)); toast(tt('ut.shRemoved', { n: x.name })); }}>×</button></div>
                <input className={s.inp34} style={{ fontWeight: 550 }} aria-label={tt('ut.shName', { i: i + 1 })} value={x.name} onChange={(e) => setShip(i, { name: e.target.value })} />
                <label className={s.qty}><span>{basis === 'gw' ? 'GW' : 'CBM'}</span><input className={s.inp34} style={{ textAlign: 'right' }} value={basis === 'gw' ? x.gw : x.cbm} onChange={(e) => setShip(i, basis === 'gw' ? { gw: e.target.value } : { cbm: e.target.value })} /></label>
                {x.job && <a className={s.jobLnk} href={`/ezy/jobs/${x.job}`}><IconLink size={16} aria-hidden="true" />{tt('ut.fromJob')} {x.job}</a>}
              </div>
            ))}
            <button type="button" className={s.addShip} onClick={() => setSh((a) => [...a, { name: `Shipper ${a.length + 1}`, cbm: '1', gw: '100' }])}><IconPlus size={16} aria-hidden="true" />Shipper</button>
          </div>
        </div>
        <div>
          <div className={s.sh}><h3>{tt('ut.fees')}</h3><button type="button" className={s.addFee} onClick={() => setFe((a) => [...a, { desc: 'OTH', amt: '0', cur: 'USD', unit: 'Unit' }])}><IconPlus size={16} aria-hidden="true" />{tt('ut.addFee')}</button></div>
          <table className={s.tbl}>
            <thead><tr><th style={{ width: '30%' }}>{tt('ut.feeCode')}</th><th style={{ width: '18%', textAlign: 'right' }}>{tt('ut.amount')}</th><th style={{ width: '13%' }}>{tt('ut.cur')}</th><th style={{ width: '15%' }}>{tt('ut.unit')}</th><th style={{ width: '18%', textAlign: 'right' }}>{tt('ut.vnd')}</th><th style={{ width: '6%' }} /></tr></thead>
            <tbody>
              {fe.map((f, i) => (
                <tr key={i} style={{ height: rowH }}>
                  <td><input className={s.inp32} style={{ fontWeight: 550 }} aria-label={tt('ut.feeCodeRow', { i: i + 1 })} value={f.desc} onChange={(e) => setFee(i, { desc: e.target.value })} /></td>
                  <td><input className={s.inp32} style={{ textAlign: 'right' }} aria-label={tt('ut.feeAmt', { i: i + 1 })} value={f.amt} onChange={(e) => setFee(i, { amt: e.target.value })} /></td>
                  <td><select className={s.inp32} aria-label={tt('ut.feeCur', { i: i + 1 })} value={f.cur} onChange={(e) => setFee(i, { cur: e.target.value as Fee['cur'] })}><option>USD</option><option>VND</option></select></td>
                  <td><select className={s.inp32} aria-label={`ĐVT dòng ${i + 1}`} value={f.unit} onChange={(e) => setFee(i, { unit: e.target.value as Fee['unit'] })}><option value="Unit">{tt('ut.unitProp')}</option><option value="Shpt">{tt('ut.unitShpt')}</option></select></td>
                  <td style={{ textAlign: 'right', fontWeight: 550 }}>{fmt(r.vnd[i] ?? 0)}</td>
                  <td style={{ textAlign: 'center' }}><button type="button" className={s.ibx28} aria-label={tt('ut.rmRow', { i: i + 1 })} onClick={() => setFe((a) => a.filter((_, j) => j !== i))}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={s.res}>
          <div className={s.sh}><h3>{tt('ut.splitRes')}</h3><span className={s.lb12}>1 USD = {fmt(rate)} ₫ · {sDate}</span></div>
          {sh.length === 0
            ? <div className={s.none}>{tt('ut.noShip')}</div>
            : (
              <table className={s.tbl2}>
                <thead><tr><th style={{ width: '18%', textAlign: 'left' }}>{tt('ut.fee')}</th>{sh.map((x, i) => <th key={i}>{x.name}</th>)}<th style={{ width: '16%' }}>{tt('ut.total')}</th></tr></thead>
                <tbody>
                  {r.rows.map((row, i) => <tr key={i}><td style={{ fontWeight: 550, textAlign: 'left' }}>{row.fee.desc} <span className={s.unit}>{row.fee.unit}</span></td>{row.alloc.map((v, j) => <td key={j}>{fmt(v)}</td>)}<td style={{ fontWeight: 550 }}>{fmt(row.alloc.reduce((a, b) => a + b, 0))}</td></tr>)}
                  <tr className={s.sum}><td style={{ textAlign: 'left' }}>{tt('ut.totalEach')}</td>{r.colTot.map((v, j) => <td key={j}>{fmt(v)}</td>)}<td>{fmt(r.grand)}</td></tr>
                </tbody>
              </table>
            )}
          <div className={s.acts}>
            <span className={`${s.lb12} ${s.grow}`}>{tt('ut.pdfNote')}</span>
            <Button icon={<IconDownload size={16} aria-hidden="true" />} onClick={() => { const n = `ChiaPhi_${sh[0]?.job ?? 'consol'}_221026.xlsx`; addMade({ ty: 'XLSX', name: n, meta: `Chia phí · ${sh.length} shipper · vừa xong` }); toast(tt('ut.exported', { n })); }}>{tt('ut.xlsx')}</Button>
          </div>
        </div>
      </div>
    </section>
  );

  // ----- Tỷ giá -----
  const uf = fxUnit(fx.from, fx.type), ut = fxUnit(fx.to, fx.type);
  const fOk = !empty && uf != null && ut != null;
  const fRate = fOk ? uf! / ut! : 0;
  const fHas = !empty;
  const codes = ['VND', ...Object.keys(FX)];
  const fxView = (
    <div className={s.row2}>
      <section className={`${s.card} ${s.fxL}`} aria-label={tt('ut.convertH')}>
        <h2 className={s.h2}>{tt('ut.convertH')}</h2>
        <label className={s.fld}><div>{tt('ut.amount')}</div><input className={s.inp} style={{ height: 44, fontSize: 18, fontWeight: 550, textAlign: 'right' }} value={fx.amt} onChange={(e) => setFx({ ...fx, amt: e.target.value })} /></label>
        <div className={s.swap}>
          <label className={s.fld}><div>{tt('ut.from')}</div><select className={s.inp} value={fx.from} onChange={(e) => setFx({ ...fx, from: e.target.value })}>{codes.map((c) => <option key={c} value={c}>{c} — {c === 'VND' ? tt('ut.vnd2') : FX[c]![0]}</option>)}</select></label>
          <button type="button" className={s.ib40} aria-label={tt('ut.swapAria')} title={tt('ut.swap')} onClick={() => setFx({ ...fx, from: fx.to, to: fx.from })}><IconArrowsExchange size={17} aria-hidden="true" /></button>
          <label className={s.fld}><div>{tt('ut.to')}</div><select className={s.inp} value={fx.to} onChange={(e) => setFx({ ...fx, to: e.target.value })}>{codes.map((c) => <option key={c} value={c}>{c} — {c === 'VND' ? 'Đồng Việt Nam' : FX[c]![0]}</option>)}</select></label>
        </div>
        <div className={s.swap}>
          <div className={`${s.fld} ${s.grow}`}><div>{tt('ut.rateType')}</div><Segmented kind="toggle" label={tt('ut.rateType')} value={fx.type} onChange={(v) => setFx({ ...fx, type: v })} items={[{ key: 'transfer', label: tt('ut.buyTransfer') }, { key: 'sell', label: tt('ut.sellRate') }]} /></div>
          <label className={s.fld}><div>{tt('ut.date')}</div><input className={s.inp} style={{ width: 130 }} value={fx.date} onChange={(e) => setFx({ ...fx, date: e.target.value })} /></label>
        </div>
        <div className={s.fxRes}>
          <div className={s.lb12}>{tt('ut.result')}</div>
          <div><b>{fOk ? fmt(num(fx.amt) * fRate, fx.to === 'VND' ? 0 : 2) : '—'}</b> <span>{fx.to}</span></div>
          <div className={s.lb12}>{fOk ? `1 ${fx.from} = ${fmt(fRate, fRate >= 1000 ? 0 : fRate >= 1 ? 2 : 6)} ${fx.to} · ${fx.type === 'sell' ? tt('ut.sellRate') : tt('ut.buyTt')} Vietcombank ${fx.date}` : tt('ut.noQuote')}</div>
        </div>
      </section>
      <section className={`${s.card} ${s.grow}`} aria-label={tt('ut.vcbTable')}>
        <div className={s.ch}><h2>{tt('ut.vcbTable')}</h2><span className={s.lb12}>{fHas ? (state === 'error' ? 'bản 21/Oct/2026 16:00 · tải lỗi' : 'cập nhật 22/Oct/2026 09:00') : 'chưa có dữ liệu'}</span></div>
        <div className={`${s.cb} ${s.cbT}`}>
          {fHas ? (
            <table className={s.tbl3}>
              <thead><tr><th style={{ width: '14%', textAlign: 'left' }}>{tt('ut.code')}</th><th style={{ width: '32%', textAlign: 'left' }}>{tt('ut.name')}</th><th>{tt('ut.buyCash')}</th><th>{tt('ut.buyTransfer')}</th><th>{tt('ut.sellRate')}</th></tr></thead>
              <tbody>
                {Object.entries(FX).map(([k, v]) => { const dp = v[3] < 100 ? 2 : 0; return (
                  <tr key={k} className={fx.from === k || fx.to === k ? s.on : ''} style={{ height: rowH }} onClick={() => setFx({ ...fx, from: k, to: 'VND' })}>
                    <td style={{ fontWeight: 600, textAlign: 'left' }}>{k}</td><td className={s.tx2} style={{ textAlign: 'left' }}>{v[0]}</td><td>{fmt(v[1], dp)}</td><td>{fmt(v[2], dp)}</td><td style={{ fontWeight: 550 }}>{fmt(v[3], dp)}</td>
                  </tr>
                ); })}
              </tbody>
            </table>
          ) : <div className={s.none}>Chưa có bảng tỷ giá cho ngày {fx.date}. Bấm “Lấy tỷ giá mới”.</div>}
        </div>
      </section>
    </div>
  );

  const dock = (
    <div className={s.dock}>
      <h2 className={s.h2} title={tt('ut.madeTip')}>{tt('ut.madeToday')}</h2>
      {made.map((m, i) => (
        <div key={`${m.name}${i}`} className={s.rec}>
          <div className={s.recH}><span className={tyCls(m.ty)} aria-hidden="true">{m.ty}</span><div className={s.fn}><div className={s.fname}>{m.name}</div><div className={s.lb12}>{m.meta}</div></div></div>
          <div className={s.recB}>
            <Button size="sm" icon={<IconEye size={14} aria-hidden="true" />} onClick={soon('Mở trình xem tệp')}>{tt('ut.view')}</Button>
            <Button size="sm" icon={<IconDownload size={14} aria-hidden="true" />} onClick={() => toast(`Đã tải ${m.name}`)}>{tt('ut.download')}</Button>
            <Button size="sm" icon={<IconLink size={14} aria-hidden="true" />} onClick={noWrite}>{tt('ut.attach')}</Button>
          </div>
        </div>
      ))}
      {made.length === 0 && <div className={s.none}>{tt('ut.noMade')}</div>}
    </div>
  );

  const body = tool === 'merge' ? merge : tool === 'ocr' ? ocrView : tool === 'convert' ? convert : tool === 'split' ? split : fxView;

  return (
    <Page kind="table" dock={wide && state !== 'loading' ? dock : undefined}>
      <PageHeader
        title={tt('ut.title')}
        actions={
          <>
            <Segmented kind="toggle" label={tt('ut.densAria')} value={dense ? 'g' : 'c'} onChange={(v) => setDense(v === 'g')} items={[{ key: 'c', label: tt('ut.dense0') }, { key: 'g', label: tt('ut.dense1') }]} />
            <HeaderTool label={tt('ut.bellAria')} icon={IconBell} n={4} onClick={soon(tt('ut.bell'))} />
            {!wide && <HeaderTool label={tt('ut.madeAria')} icon={IconLayoutSidebarRight} active={right} onClick={() => setRight((o) => !o)} />}
            {pri.k && <Button variant="primary" icon={tool === 'split' ? <IconSend size={16} aria-hidden="true" /> : <IconPlus size={16} aria-hidden="true" />} disabled={pri.dis || !showTool} onClick={pri.run}>{pri.k}</Button>}
          </>
        }
      />
      <div className={!wide && dockOpen && state !== 'loading' ? s.split : undefined}>
        <div className={s.page}>
          <div className={s.tabs}>
            <Segmented label={tt('ut.title')} value={tool} onChange={setTool} items={TOOLS.map(([key, label]) => ({ key, label: locked(key) ? <>{label}<IconLock size={12} aria-label={tt('ut.noPerm')} /></> : label }))} />
            <span className={s.lb12}>{SUB[tool]}</span>
          </div>
          {state === 'error' && (
            <div role="alert" className={s.err}><span aria-hidden="true">!</span><div><b>{tt('ut.errHead')}</b> {tt('ut.errTail')}</div><Button onClick={() => location.reload()}>{tt('ut.retry')}</Button></div>
          )}
          {loading && <StateView state="loading" skeleton={<Skeleton />} />}
          {locked(tool) && state === 'noperm' && (
            <StateView state="noperm" title={tt('ut.permTitle', { w: tool === 'ocr' ? tt('ut.wOcr') : tt('ut.wConv') })} desc={tt('ut.permDesc')} action={<Button icon={<IconSend size={16} aria-hidden="true" />} onClick={() => toast(tt('ut.reqSent'))}>{tt('ut.reqPerm')}</Button>} />
          )}
          {showTool && body}
        </div>
        {!wide && dockOpen && state !== 'loading' && dock}
      </div>
    </Page>
  );
}
